import type React from 'react';

import type {
  BannerRxnOnlyProps,
  BannerWebOnlyProps,
  Props as BannerProps,
} from '@rover/kibble/official-patterns/Banner';
import type { BannerSeverity } from '@rover/kibble/official-patterns/Banner/BannerSeverity.constants';

/**
 * `bottom`/`top` pin to that edge; `inline` hands positioning to the parent and fades.
 * A pinned native toast must mount at the root of the screen: RxN has no `position: fixed`.
 */
export type ToastPlacement = 'bottom' | 'top' | 'inline';

/**
 * The one action a toast may carry, such as Undo or View: `href` navigates, `onPress` acts in
 * place, and either dismisses the toast, whose message is spent once the reader has acted on it.
 */
export type ToastLink = {
  label: string;
  href?: string;
  onPress?: () => void;
};

/** The omitted Banner props don't apply to a transient toast; `fontSize` is pinned. */
type BaseProps = Omit<
  BannerProps,
  'onPress' | 'FooterComponent' | 'suppressDefaultTypography' | 'fontSize' | 'webProps' | 'rxnProps'
> & {
  /** Rendered under the message. `FooterComponent` is closed off so there is only ever one. */
  link?: ToastLink;
  autoHideDuration?: number;
  placement?: ToastPlacement;
  /** Pass `false` only when the caller already owns a live region for the same message. */
  announce?: boolean;
  /** Set by `ToastStack`, which owns position and insets; the toast keeps only its slide. */
  isStacked?: boolean;
  /** Freezes the auto-hide timer, ORed with the toast's own hover / focus state. */
  isPaused?: boolean;
  /** Restarts the auto-hide timer when it changes; a replaced toast keeps its `id`. */
  timerEpoch?: number;
  /** Called as the toast starts leaving, where `onClose` is the back of the exit. */
  onExitStart?: () => void;
};

export type ToastWebOnlyProps = BannerWebOnlyProps;

export type ToastRxnOnlyProps = BannerRxnOnlyProps;

export type Props = BaseProps & {
  webProps?: Partial<Omit<BaseProps, keyof ToastWebOnlyProps>> & ToastWebOnlyProps;
  rxnProps?: Partial<Omit<BaseProps, keyof ToastRxnOnlyProps>> & ToastRxnOnlyProps;
};

export type ToastHandle = {
  /** `accelerated` is the fast exit a stack uses to evict. */
  dismiss: (options?: { accelerated?: boolean }) => void;
};

export type ShowToastOptions = {
  message: string;
  severity?: `${BannerSeverity}`;
  title?: React.ReactNode;
  link?: ToastLink;
  autoHideDuration?: number;
  /** Identifies the toast's source: a second call with the same key replaces it in place. */
  key?: string;
};

export type ToastRecord = ShowToastOptions & {
  id: string;
  timerEpoch: number;
  /** Set by `dismissToast`; the stack answers by sending the toast on its exit. */
  isDismissing?: boolean;
  /** Set as the toast begins its exit; a same-`key` call then starts a new toast. */
  isExiting?: boolean;
};

/** Punctuation a title may already end in, which the joined announcement must not double. */
const SENTENCE_END = /[.!?:;]$/;

const joinSpoken = (first: string, second: string): string =>
  `${first}${SENTENCE_END.test(first) ? '' : '.'} ${second}`;

/**
 * Title, message, then the action's label, which a reader would otherwise never hear: the toast
 * is gone before they could find it. A JSX title cannot be flattened to speech, so it is left out.
 */
export const getToastAnnouncement = ({
  title,
  message,
  link,
}: Pick<ToastRecord, 'title' | 'message' | 'link'>): string => {
  const spoken = typeof title === 'string' && title !== '' ? joinSpoken(title, message) : message;

  return link ? joinSpoken(spoken, link.label) : spoken;
};

export const DEFAULT_AUTO_HIDE_DURATION = 8000;

export const ANIMATION_DURATION = 400;

/** Outlasts the slide so it finishes before `onClose` unmounts the toast. */
export const EXIT_DURATION = ANIMATION_DURATION + 100;

export const ACCELERATED_ANIMATION_DURATION = 150;

export const ACCELERATED_EXIT_DURATION = ACCELERATED_ANIMATION_DURATION + 100;

/** A further arrival evicts the oldest rather than queueing. */
export const MAX_VISIBLE_TOASTS = 3;

/** 8px, per the stacking spec. */
export const TOAST_STACK_GAP = '2x';

/** Travel between slots. Shorter than an entry: only where the toast sits is changing. */
export const SLOT_SHIFT_DURATION = 200;

/** Lets the mount settle before speaking, so the announcement is not cut off. */
export const ANNOUNCE_DELAY = 100;

/** Minimum gap between one batch of announcements and the next. */
export const ANNOUNCE_INTERVAL = 150;

export const DEFAULT_PLACEMENT: ToastPlacement = 'bottom';

/** Distance from the pinned edge, and the side inset below TOAST_MAX_WIDTH. */
export const TOAST_EDGE_OFFSET = '4x';

export const TOAST_WIDTH = '100%';

/** No token fits: the `sizes` scale tops out at 64x (256px). */
export const TOAST_MAX_WIDTH = '400px';

export const TOAST_FONT_SIZE = '200' as const;
